import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Star, Loader2, AlertCircle, CheckCircle2, Mountain, Smile, Frown, MessageSquare, Globe } from 'lucide-react';
import { Language, LANGUAGE_NAMES } from '../translations';

interface ReviewTranslations {
    title: string;
    subtitle: string;
    ratingLabel: string;
    ratingSub: Record<number, string>;
    positiveLabel: string;
    positivePlaceholder: string;
    negativeLabel: string;
    negativePlaceholder: string;
    generalLabel: string;
    generalPlaceholder: string;
    submitBtn: string;
    submittingBtn: string;
    successTitle: string;
    successSub: string;
    successText: string;
    alreadyTitle: string;
    alreadySub: string;
    alreadyInfo: string;
    backBtn: string;
    errorAccess: string;
    verifying: string;
    disclaimer: string;
    selectLanguage: string;
}

const REVIEW_LOCALES: Record<Language, ReviewTranslations> = {
    es: {
        title: 'Evalúa tu Experiencia',
        subtitle: 'Hola {name}, comparte tu testimonio de {expedition}.',
        ratingLabel: 'Califica tu Expedición General *',
        ratingSub: {
            1: 'Muy Insatisfecho/a',
            2: 'Insatisfecho/a',
            3: 'Aceptable',
            4: 'Excelente',
            5: '¡Increíble, Superó Expectativas!'
        },
        positiveLabel: 'Opiniones positivas',
        positivePlaceholder: 'Describe lo que más te gustó (los guías, la comida, los paisajes, la organización, etc.)...',
        negativeLabel: 'Aspectos a mejorar',
        negativePlaceholder: 'Cuéntanos qué podríamos perfeccionar para futuras salidas...',
        generalLabel: 'Comentarios generales',
        generalPlaceholder: 'Escribe un resumen general o comentarios adicionales...',
        submitBtn: 'Enviar Evaluación ➔',
        submittingBtn: 'Enviando...',
        successTitle: '¡Evaluación Enviada!',
        successSub: 'Tu opinión ha sido registrada con éxito',
        successText: 'Gracias {name}, tu reseña de la expedición {expedition} ya está en nuestro sistema. Nos ayuda inmensamente a mantener nuestro estándar de montaña.',
        alreadyTitle: '¡Muchas Gracias!',
        alreadySub: 'Ya has calificado tu expedición a {expedition}',
        alreadyInfo: 'Tu opinión ya está registrada en nuestro sistema de calidad y nos ayuda a perfeccionar la experiencia premium en la montaña.',
        backBtn: 'Terminar y Volver',
        errorAccess: 'No pudimos encontrar una reserva válida con este identificador.',
        verifying: 'Verificando Reserva...',
        disclaimer: '* Al enviar, autorizas a Fisura Expeditions a mostrar este testimonio de forma anónima en nuestra web pública.',
        selectLanguage: 'Seleccionar idioma'
    },
    en: {
        title: 'Rate Your Experience',
        subtitle: 'Hello {name}, share your testimony of {expedition}.',
        ratingLabel: 'Rate Your Overall Expedition *',
        ratingSub: {
            1: 'Very Dissatisfied',
            2: 'Dissatisfied',
            3: 'Acceptable',
            4: 'Excellent',
            5: 'Amazing, Exceeded Expectations!'
        },
        positiveLabel: 'Positive opinions',
        positivePlaceholder: 'Describe what you liked most (the guides, the food, the landscapes, the organization, etc.)...',
        negativeLabel: 'Areas for improvement',
        negativePlaceholder: 'Tell us what we could improve for future outings...',
        generalLabel: 'General comments',
        generalPlaceholder: 'Write a general summary or additional comments...',
        submitBtn: 'Submit Review ➔',
        submittingBtn: 'Submitting...',
        successTitle: 'Review Submitted!',
        successSub: 'Your feedback has been successfully recorded',
        successText: 'Thank you {name}, your review for {expedition} is now in our system. It helps us immensely to maintain our mountain standards.',
        alreadyTitle: 'Thank You Very Much!',
        alreadySub: 'You have already rated your expedition to {expedition}',
        alreadyInfo: 'Your opinion is already registered in our quality system and helps us perfect the premium mountain experience.',
        backBtn: 'Finish and Return',
        errorAccess: 'We could not find a valid booking with this identifier.',
        verifying: 'Verifying Booking...',
        disclaimer: '* By submitting, you authorize Fisura Expeditions to display this testimony anonymously on our public website.',
        selectLanguage: 'Select language'
    },
    pt: {
        title: 'Avalie sua Experiência',
        subtitle: 'Olá {name}, compartilhe seu depoimento sobre {expedition}.',
        ratingLabel: 'Avalie sua Expedição Geral *',
        ratingSub: {
            1: 'Muito Insatisfeito/a',
            2: 'Insatisfeito/a',
            3: 'Aceitável',
            4: 'Excelente',
            5: 'Incrível, superou as expectativas!'
        },
        positiveLabel: 'Opiniões positivas',
        positivePlaceholder: 'Descreva o que você mais gostou (os guias, a comida, as paisagens, a organização, etc.)...',
        negativeLabel: 'Aspectos a melhorar',
        negativePlaceholder: 'Diga-nos o que poderíamos melhorar para futuras saídas...',
        generalLabel: 'Comentários gerais',
        generalPlaceholder: 'Escreva um resumo geral ou comentários adicionais...',
        submitBtn: 'Enviar Avaliação ➔',
        submittingBtn: 'Enviando...',
        successTitle: 'Avaliação Enviada!',
        successSub: 'Sua opinião foi registrada com sucesso',
        successText: 'Obrigado {name}, sua avaliação da expedição {expedition} já está em nosso sistema. Nos ajuda imensamente a manter nosso padrão de montanha.',
        alreadyTitle: 'Muito Obrigado!',
        alreadySub: 'Você já avaliou sua expedição para {expedition}',
        alreadyInfo: 'Sua opinião já está registrada em nosso sistema de qualidade e nos ajuda a aperfeiçoar a experiência premium na Patagônia.',
        backBtn: 'Terminar e Voltar',
        errorAccess: 'Não conseguimos encontrar uma reserva válida com este identificador.',
        verifying: 'Verificando Reserva...',
        disclaimer: '* Ao enviar, você autoriza a Fisura Expeditions a exibir este depoimento anonimamente em nosso site público.',
        selectLanguage: 'Selecionar idioma'
    },
    fr: {
        title: 'Évaluez votre Expérience',
        subtitle: 'Bonjour {name}, partagez votre témoignage sur {expedition}.',
        ratingLabel: 'Évaluez votre Expédition Globale *',
        ratingSub: {
            1: 'Très Insatisfait(e)',
            2: 'Insatisfait(e)',
            3: 'Acceptable',
            4: 'Excellent',
            5: 'Incroyable, a dépassé les attentes !'
        },
        positiveLabel: 'Opinions positives',
        positivePlaceholder: 'Décrivez ce que vous avez le plus aimé (les guides, la nourriture, les paysages, l\'organisation, etc.)...',
        negativeLabel: 'Points à améliorer',
        negativePlaceholder: 'Dites-nous ce que nous pourrions améliorer pour les prochaines sorties...',
        generalLabel: 'Commentaires généraux',
        generalPlaceholder: 'Écrivez un résumé général ou des commentaires supplémentaires...',
        submitBtn: 'Envoyer l\'Évaluation ➔',
        submittingBtn: 'Envoi en cours...',
        successTitle: 'Évaluation Envoyée !',
        successSub: 'Votre avis a été enregistré avec succès',
        successText: 'Merci {name}, votre avis sur l\'expédition {expedition} est maintenant dans notre système. Cela nous aide énormément à maintenir nos standards de montagne.',
        alreadyTitle: 'Merci Beaucoup !',
        alreadySub: 'Vous avez déjà évalué votre expédition {expedition}',
        alreadyInfo: 'Votre avis est déjà enregistré dans notre système qualité et nous aide à perfectionner l\'expérience premium en Patagonie.',
        backBtn: 'Terminer et Retourner',
        errorAccess: 'Nous n\'avons pas pu trouver de réservation valide avec cet identifiant.',
        verifying: 'Vérification de la Réservation...',
        disclaimer: '* En envoyant, vous autorisez Fisura Expeditions à afficher ce témoignage de manière anonyme sur notre site public.',
        selectLanguage: 'Choisir la langue'
    },
    it: {
        title: 'Valuta la tua Esperienza',
        subtitle: 'Ciao {name}, condividi la tua testimonianza di {expedition}.',
        ratingLabel: 'Valuta la tua Spedizione Generale *',
        ratingSub: {
            1: 'Molto Insoddisfatto/a',
            2: 'Insoddisfatto/a',
            3: 'Accettabile',
            4: 'Eccellente',
            5: 'Incredibile, ha superato le aspettative!'
        },
        positiveLabel: 'Opinioni positive',
        positivePlaceholder: 'Descrivi cosa ti è piaciuto di più (le guide, il cibo, i paesaggi, l\'organizzazione, ecc.)...',
        negativeLabel: 'Aspetti da migliorare',
        negativePlaceholder: 'Dicci cosa potremmo migliorare per le future uscite...',
        generalLabel: 'Commenti generali',
        generalPlaceholder: 'Scrivi un riassunto generale o commenti aggiuntivi...',
        submitBtn: 'Invia Valutazione ➔',
        submittingBtn: 'Invio...',
        successTitle: 'Valutazione Inviata!',
        successSub: 'La tua opinione è stata registrata con successo',
        successText: 'Grazie {name}, la tua recensione della spedizione {expedition} è ora nel nostro sistema. Ci aiuta immensamente a mantenere i nostri standard di montagna.',
        alreadyTitle: 'Grazie Mille!',
        alreadySub: 'Hai già valutato la tua spedizione a {expedition}',
        alreadyInfo: 'La tua opinione è già registrata nel nostro sistema di qualità e ci aiuta a perfezionare l\'esperienza premium in montagna.',
        backBtn: 'Termina e Ritorna',
        errorAccess: 'Non siamo riusciti a trovare una prenotazione valida con questo identificatore.',
        verifying: 'Verifica Prenotazione...',
        disclaimer: '* Inviando, autorizzi Fisura Expeditions a mostrare questa testimonianza in modo anonimo sul nostro sito web pubblico.',
        selectLanguage: 'Seleziona lingua'
    },
    de: {
        title: 'Bewerten Sie Ihre Erfahrung',
        subtitle: 'Hallo {name}, teilen Sie Ihren Erfahrungsbericht über {expedition}.',
        ratingLabel: 'Bewerten Sie Ihre Gesamte Expedition *',
        ratingSub: {
            1: 'Sehr Unzufrieden',
            2: 'Unzufrieden',
            3: 'Akzeptabel',
            4: 'Exzellent',
            5: 'Unglaublich, hat die Erwartungen übertroffen!'
        },
        positiveLabel: 'Positive Meinungen',
        positivePlaceholder: 'Beschreiben Sie, was Ihnen am besten gefallen hat (die Guides, das Essen, die Landschaften, die Organisation usw.)...',
        negativeLabel: 'Verbesserungswürdige Aspekte',
        negativePlaceholder: 'Sagen Sie uns, was wir für zukünftige Ausflüge verbessern könnten...',
        generalLabel: 'Allgemeine Kommentare',
        generalPlaceholder: 'Schreiben Sie eine allgemeine Zusammenfassung oder zusätzliche Kommentare...',
        submitBtn: 'Bewertung Absenden ➔',
        submittingBtn: 'Wird gesendet...',
        successTitle: 'Bewertung Abgegeben!',
        successSub: 'Ihre Meinung wurde erfolgreich registriert',
        successText: 'Danke {name}, Ihre Bewertung für die Expedition {expedition} ist jetzt in unserem System. Es hilft uns ungemein, unseren Bergstandard zu halten.',
        alreadyTitle: 'Vielen Dank!',
        alreadySub: 'Sie haben Ihre Expedition nach {expedition} bereits bewertet',
        alreadyInfo: 'Ihre Meinung ist bereits in unserem Qualitätssystem registriert und hilft uns, das Premium-Erlebnis in Patagonien zu perfektionieren.',
        backBtn: 'Beenden und Zurück',
        errorAccess: 'Wir konnten keine gültige Buchung mit dieser Kennung finden.',
        verifying: 'Buchung überprüfen...',
        disclaimer: '* Mit dem Absenden ermächtigen Sie Fisura Expeditions, diesen Erfahrungsbericht anonym auf unserer öffentlichen Website anzuzeigen.',
        selectLanguage: 'Sprache auswählen'
    }
};

interface ReviewPageProps {
    expeditions?: any[];
}

const ReviewPage: React.FC<ReviewPageProps> = ({ expeditions = [] }) => {
    const { bookingId } = useParams<{ bookingId: string }>();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [booking, setBooking] = useState<any | null>(null);
    const [existingReview, setExistingReview] = useState<any | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Translation/Language State
    const [currentLang, setCurrentLang] = useState<Language>('es');

    // Form State
    const [rating, setRating] = useState<number>(0);
    const [hoverRating, setHoverRating] = useState<number>(0);
    
    // Split Comments State
    const [positiveComments, setPositiveComments] = useState<string>('');
    const [negativeComments, setNegativeComments] = useState<string>('');
    const [generalComments, setGeneralComments] = useState<string>('');
    
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        // Detect browser language
        const browserLang = navigator.language || (navigator as any).userLanguage || 'es';
        const code = browserLang.split('-')[0].toLowerCase() as Language;
        if (['es', 'en', 'pt', 'fr', 'it', 'de'].includes(code)) {
            setCurrentLang(code);
        } else {
            setCurrentLang('es');
        }
    }, []);

    useEffect(() => {
        const verifyBooking = async () => {
            if (!bookingId) {
                setError('ID de reserva inválido o no provisto.');
                setLoading(false);
                return;
            }

            if (bookingId === 'test') {
                setBooking({
                    id: 'test',
                    expedition_slug: 'laguna',
                    payment_status: 'paid',
                    participants: [{ fullName: 'Explorador de Prueba' }]
                });
                setLoading(false);
                return;
            }

            try {
                // 1. Fetch booking
                const { data: bookingData, error: bookingErr } = await supabase
                    .from('bookings')
                    .select('*')
                    .eq('id', bookingId)
                    .single();

                if (bookingErr || !bookingData) {
                    setError('No pudimos encontrar una reserva válida con este identificador.');
                    setLoading(false);
                    return;
                }

                // If booking is cancelled, don't allow review
                if (bookingData.payment_status === 'cancelled') {
                    setError('Esta reserva ha sido cancelada y no puede ser evaluada.');
                    setLoading(false);
                    return;
                }

                setBooking(bookingData);

                // 2. Check if already reviewed
                const { data: reviewData, error: reviewErr } = await supabase
                    .from('reviews')
                    .select('*')
                    .eq('booking_id', bookingId)
                    .maybeSingle();

                if (reviewData) {
                    setExistingReview(reviewData);
                }
            } catch (err: any) {
                console.error('Error verifying review access:', err);
                setError('Ocurrió un error al verificar la reserva. Inténtalo más tarde.');
            } finally {
                setLoading(false);
            }
        };

        verifyBooking();
    }, [bookingId]);

    const getExpedicionName = (slug: string) => {
        const exp = (expeditions || []).find((e: any) => e.id === slug || e.slug === slug);
        return exp ? (exp.name_es || exp.name_en) : slug;
    };

    const getLeadName = () => {
        if (!booking || !booking.participants || booking.participants.length === 0) return 'Explorador';
        return booking.participants[0].fullName || 'Explorador';
    };

    const tReview = REVIEW_LOCALES[currentLang] || REVIEW_LOCALES.es;

    const formatText = (text: string, vars: Record<string, string>) => {
        let result = text;
        for (const [key, value] of Object.entries(vars)) {
            result = result.replace(`{${key}}`, value);
        }
        return result;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (rating === 0) {
            alert('Por favor selecciona una calificación de estrellas.');
            return;
        }

        setIsSubmitting(true);

        if (bookingId === 'test') {
            await new Promise(resolve => setTimeout(resolve, 800));
            setSubmitted(true);
            setIsSubmitting(false);
            return;
        }

        try {
            // Format combined comments Booking.com style
            const combinedComments = `😊 POSITIVO:\n${positiveComments.trim()}\n\n😞 A MEJORAR:\n${negativeComments.trim()}\n\n💬 GENERAL:\n${generalComments.trim()}`;

            const reviewPayload = {
                booking_id: bookingId,
                expedition_slug: booking.expedition_slug,
                rating,
                customer_name: getLeadName(),
                comments: combinedComments
            };

            const { error: insertErr } = await supabase
                .from('reviews')
                .insert([reviewPayload]);

            if (insertErr) throw insertErr;

            // Send email notification to hello@fisuraexpeditions.com using the Edge Function
            try {
                await supabase.functions.invoke('booking-status-email', {
                    body: {
                        new_status: 'review_submitted',
                        customer_name: getLeadName(),
                        customer_email: 'hello@fisuraexpeditions.com',
                        booking_id: bookingId,
                        booking_details: {
                            expedition_name: getExpedicionName(booking.expedition_slug),
                            rating: String(rating),
                            comments: combinedComments
                        }
                    }
                });
                console.log("Review notification email successfully triggered!");
            } catch (emailErr) {
                console.error("Error triggering review notification email:", emailErr);
            }

            // Google Analytics tracking
            if (typeof window !== 'undefined' && (window as any).gtag) {
                (window as any).gtag('event', 'submit_review', {
                    rating: rating,
                    expedition_slug: booking.expedition_slug,
                    customer_name: getLeadName()
                });
            }

            setSubmitted(true);
        } catch (err: any) {
            console.error('Error submitting review:', err);
            alert(`Error al enviar evaluación: ${err.message || err}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center text-white px-4">
                <Loader2 className="w-10 h-10 animate-spin text-[#00d4ff] mb-4" />
                <p className="font-title text-lg uppercase tracking-widest text-gray-400">{tReview.verifying}</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center text-white px-4 text-center">
                <div className="bg-red-500/10 border border-red-500/30 p-8 rounded-[10px] max-w-md space-y-4">
                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
                    <h2 className="text-2xl font-title uppercase tracking-tight text-white">Acceso Denegado</h2>
                    <p className="text-xs text-gray-400 leading-relaxed uppercase tracking-wider">{error}</p>
                    <button 
                        onClick={() => navigate('/')}
                        className="mt-4 px-6 py-3 bg-white text-black font-title text-xs uppercase tracking-widest hover:bg-[#00d4ff] transition-all rounded-[8px]"
                    >
                        Volver al Home
                    </button>
                </div>
            </div>
        );
    }

    if (existingReview) {
        return (
            <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center text-white px-4 text-center">
                <div className="bg-[#1a1512] border border-rock p-8 md:p-12 rounded-[10px] max-w-xl space-y-6 shadow-2xl">
                    <Mountain className="w-16 h-16 text-[#25D366] mx-auto animate-pulse" />
                    <div className="space-y-2">
                        <h2 className="text-3xl font-title uppercase tracking-tighter text-white">{tReview.alreadyTitle}</h2>
                        <p className="text-gray-400 text-xs uppercase tracking-widest font-bold">
                            {formatText(tReview.alreadySub, { expedition: getExpedicionName(booking.expedition_slug) })}
                        </p>
                    </div>

                    <div className="bg-black/30 p-5 rounded border border-rock/50 max-w-md mx-auto">
                        <div className="flex items-center justify-center gap-1 mb-2">
                            {Array.from({ length: 5 }).map((_, i) => (
                                <Star 
                                    key={i} 
                                    className={`w-5 h-5 ${i < existingReview.rating ? 'fill-yellow-500 text-yellow-500' : 'text-gray-600'}`} 
                                />
                            ))}
                        </div>
                        {existingReview.comments && (
                            <p className="text-xs text-gray-400 italic font-light whitespace-pre-line text-left">
                                {existingReview.comments}
                            </p>
                        )}
                    </div>

                    <p className="text-gray-500 text-[10px] uppercase tracking-widest leading-relaxed">
                        {tReview.alreadyInfo}
                    </p>
                    
                    <button 
                        onClick={() => navigate('/')}
                        className="px-8 py-4 bg-white text-black font-title text-xs uppercase tracking-widest hover:bg-[#00d4ff] transition-all rounded-[8px]"
                    >
                        {tReview.backBtn}
                    </button>
                </div>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center text-white px-4 text-center">
                <div className="bg-[#1a1512] border border-[#25D366]/30 p-8 md:p-12 rounded-[10px] max-w-xl space-y-8 shadow-2xl animate-in fade-in zoom-in-95 duration-500">
                    <div className="w-20 h-20 bg-[#25D366]/10 rounded-full flex items-center justify-center mx-auto border border-[#25D366]/30">
                        <CheckCircle2 className="w-10 h-10 text-[#25D366]" />
                    </div>
                    
                    <div className="space-y-3">
                        <h2 className="text-4xl font-title uppercase tracking-tighter text-white">
                            {tReview.successTitle}
                        </h2>
                        <p className="text-[#25D366] text-xs uppercase tracking-widest font-black">
                            {tReview.successSub}
                        </p>
                        <p className="text-gray-400 text-xs uppercase tracking-wider leading-relaxed max-w-md mx-auto pt-2">
                            {formatText(tReview.successText, { 
                                name: getLeadName(), 
                                expedition: getExpedicionName(booking.expedition_slug) 
                            })}
                        </p>
                    </div>

                    {/* Engaging elements / items of value */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-6 border-t border-rock/50">
                        <div className="bg-black/40 p-5 rounded-[10px] border border-rock/40 text-left space-y-2">
                            <span className="text-[10px] text-[#00d4ff] font-bold uppercase tracking-widest">¿Listos para otra aventura?</span>
                            <h4 className="text-sm font-title uppercase text-white">Próximas Salidas</h4>
                            <p className="text-[9px] text-gray-500 leading-normal uppercase tracking-wide">Descubre nuestras expediciones programadas de trekking y escalada en roca.</p>
                            <button 
                                onClick={() => navigate('/expeditions')}
                                className="w-full mt-2 py-3 bg-white text-black font-title text-[9px] uppercase tracking-widest hover:bg-[#00d4ff] transition-all rounded-[6px] font-bold"
                            >
                                Ver Expediciones
                            </button>
                        </div>
                        <div className="bg-black/40 p-5 rounded-[10px] border border-rock/40 text-left space-y-2">
                            <span className="text-[10px] text-[#25D366] font-bold uppercase tracking-widest">Comunidad de montaña</span>
                            <h4 className="text-sm font-title uppercase text-white">Síguenos en Instagram</h4>
                            <p className="text-[9px] text-gray-500 leading-normal uppercase tracking-wide">Mira fotos en tiempo real y entérate de las condiciones climáticas en Aysén.</p>
                            <a 
                                href="https://instagram.com/fisuraexpeditions" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="w-full block text-center mt-2 py-3 bg-[#25D366] text-black font-title text-[9px] uppercase tracking-widest hover:bg-[#128C7E] transition-all rounded-[6px] font-bold decoration-none"
                            >
                                @fisuraexpeditions
                            </a>
                        </div>
                    </div>

                    <button 
                        onClick={() => navigate('/')}
                        className="w-full py-4 border border-rock text-[10px] font-black uppercase tracking-widest transition-all rounded-[10px] hover:bg-white hover:text-black hover:border-white shadow-md text-white mt-4"
                    >
                        {tReview.backBtn}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0a0a0a] pt-32 pb-24 px-4 sm:px-6">
            <div className="max-w-2xl mx-auto">
                
                {/* Language Selector */}
                <div className="flex justify-end mb-6">
                    <div className="flex items-center gap-2 bg-[#1a1512] border border-rock px-3 py-2 rounded-[8px] shadow-lg">
                        <Globe className="w-4 h-4 text-[#00d4ff]" />
                        <select
                            value={currentLang}
                            onChange={(e) => setCurrentLang(e.target.value as Language)}
                            className="bg-transparent text-[11px] text-white uppercase tracking-wider font-bold border-none outline-none cursor-pointer appearance-none pr-1"
                        >
                            {Object.entries(LANGUAGE_NAMES).map(([code, name]) => (
                                <option key={code} value={code} className="bg-[#1a1512] text-white">
                                    {name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Header */}
                <div className="text-center space-y-4 mb-10">
                    <div className="w-12 h-12 bg-[#00d4ff]/10 rounded-full flex items-center justify-center mx-auto mb-2 border border-[#00d4ff]/30">
                        <Mountain className="w-6 h-6 text-[#00d4ff]" />
                    </div>
                    <h1 className="text-4xl md:text-5xl font-title uppercase tracking-tighter text-white">
                        {tReview.title}
                    </h1>
                    <p className="text-gray-400 text-xs uppercase tracking-[0.25em] font-bold max-w-md mx-auto leading-relaxed">
                        {formatText(tReview.subtitle, { name: getLeadName(), expedition: getExpedicionName(booking.expedition_slug) })}
                    </p>
                </div>

                {/* Form card */}
                <form onSubmit={handleSubmit} className="bg-[#1a1512] border border-rock p-8 md:p-12 shadow-2xl relative space-y-8 rounded-[10px]">
                    {/* Stars Selector */}
                    <div className="text-center space-y-3">
                        <label className="text-[10px] text-gray-400 uppercase tracking-widest font-bold block mb-2">
                            {tReview.ratingLabel}
                        </label>
                        <div className="flex justify-center items-center gap-2">
                            {Array.from({ length: 5 }).map((_, i) => {
                                const starValue = i + 1;
                                const isActive = starValue <= (hoverRating || rating);
                                return (
                                    <button
                                        key={i}
                                        type="button"
                                        onClick={() => setRating(starValue)}
                                        onMouseEnter={() => setHoverRating(starValue)}
                                        onMouseLeave={() => setHoverRating(0)}
                                        className="p-1 focus:outline-none transition-transform hover:scale-125 duration-200"
                                    >
                                        <Star 
                                            className={`w-10 h-10 sm:w-12 sm:h-12 transition-all ${
                                                isActive 
                                                    ? 'fill-yellow-500 text-yellow-500 filter drop-shadow-[0_0_8px_rgba(234,179,8,0.3)]' 
                                                    : 'text-gray-700'
                                            }`} 
                                        />
                                    </button>
                                );
                            })}
                        </div>
                        {rating > 0 && (
                            <p className="text-[9px] text-[#00d4ff] uppercase tracking-widest font-black mt-2">
                                {tReview.ratingSub[rating]}
                            </p>
                        )}
                    </div>

                    {/* Booking.com Style Questions */}
                    <div className="space-y-6 pt-4 border-t border-rock/30">
                        {/* 1. Opiniones Positivas */}
                        <div className="space-y-2">
                            <label className="text-[10px] text-gray-300 uppercase tracking-widest font-bold flex items-center gap-2">
                                <Smile className="w-4 h-4 text-[#25D366]" />
                                {tReview.positiveLabel}
                            </label>
                            <textarea
                                rows={3}
                                value={positiveComments}
                                onChange={(e) => setPositiveComments(e.target.value)}
                                placeholder={tReview.positivePlaceholder}
                                className="w-full bg-black border border-rock p-4 text-xs text-white placeholder:text-gray-600 outline-none focus:border-[#25D366] transition-colors resize-none rounded-[8px]"
                            ></textarea>
                        </div>

                        {/* 2. Aspectos a Mejorar */}
                        <div className="space-y-2">
                            <label className="text-[10px] text-gray-300 uppercase tracking-widest font-bold flex items-center gap-2">
                                <Frown className="w-4 h-4 text-orange-500" />
                                {tReview.negativeLabel}
                            </label>
                            <textarea
                                rows={3}
                                value={negativeComments}
                                onChange={(e) => setNegativeComments(e.target.value)}
                                placeholder={tReview.negativePlaceholder}
                                className="w-full bg-black border border-rock p-4 text-xs text-white placeholder:text-gray-600 outline-none focus:border-orange-500/80 transition-colors resize-none rounded-[8px]"
                            ></textarea>
                        </div>

                        {/* 3. Comentarios Generales */}
                        <div className="space-y-2">
                            <label className="text-[10px] text-gray-300 uppercase tracking-widest font-bold flex items-center gap-2">
                                <MessageSquare className="w-4 h-4 text-[#00d4ff]" />
                                {tReview.generalLabel}
                            </label>
                            <textarea
                                rows={3}
                                value={generalComments}
                                onChange={(e) => setGeneralComments(e.target.value)}
                                placeholder={tReview.generalPlaceholder}
                                className="w-full bg-black border border-rock p-4 text-xs text-white placeholder:text-gray-600 outline-none focus:border-[#00d4ff] transition-colors resize-none rounded-[8px]"
                            ></textarea>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={isSubmitting || rating === 0}
                        className="w-full h-16 bg-[#f2f2f2] text-black font-title text-xl hover:bg-[#00d4ff] disabled:opacity-20 transition-all uppercase tracking-tight flex items-center justify-center gap-2 rounded-[8px]"
                    >
                        {isSubmitting ? (
                            <>
                                <Loader2 className="w-5 h-5 animate-spin" />
                                {tReview.submittingBtn}
                            </>
                        ) : (
                            tReview.submitBtn
                        )}
                    </button>

                    <div className="text-center">
                        <p className="text-[8px] text-gray-600 uppercase tracking-widest italic">
                            {tReview.disclaimer}
                        </p>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ReviewPage;
